import { get, patch, del } from 'aws-amplify/api';
import useSWR, { mutate } from 'swr';
import { processResponse } from './api_util';

// Client for the Livi Files API: Word / Excel documents Livi created on the
// case. See .claude/references/livi-documents.md (ADR-001, D5). Records carry the
// S3 key, not a URL: the browser signs on demand.
const API_NAME = 'LivisiblyLiviDocumentsApi';
const LIST_KEY = 'LIVI_DOCUMENTS';

const queryString = (filters = {}) => {
  const params = Object.entries(filters)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  return params.length ? `?${params.join('&')}` : '';
};

export async function listLiviDocuments(filters = {}) {
  const restOperation = get({ apiName: API_NAME, path: `livi_documents${queryString(filters)}` });
  const response = await restOperation.response;
  return processResponse(response);
}

export async function getLiviDocument(documentId) {
  const restOperation = get({ apiName: API_NAME, path: `livi_documents/${encodeURIComponent(documentId)}` });
  const response = await restOperation.response;
  return processResponse(response);
}

// Filters: { format, sessionID, topicID, since, until }. Keyed on the case so a
// case switch never shows another case's list.
export function useLiviDocuments(caseId, filters = {}, shouldFetch = true) {
  const qs = queryString(filters);
  return useSWR(shouldFetch && caseId ? [LIST_KEY, caseId, qs] : null, () => listLiviDocuments(filters));
}

export function useLiviDocument(documentId, shouldFetch = true) {
  return useSWR(
    shouldFetch && documentId ? ['LIVI_DOCUMENT', documentId] : null,
    ([, id]) => getLiviDocument(id),
    { revalidateOnFocus: false }
  );
}

const refreshLists = () => mutate((key) => Array.isArray(key) && key[0] === LIST_KEY);

export async function renameLiviDocument(documentId, title) {
  const restOperation = patch({
    apiName: API_NAME,
    path: `livi_documents/${encodeURIComponent(documentId)}`,
    options: { body: { title } },
  });
  const response = await restOperation.response;
  const result = await processResponse(response);
  await refreshLists();
  mutate(['LIVI_DOCUMENT', documentId]);
  return result;
}

export async function deleteLiviDocument(documentId) {
  const restOperation = del({ apiName: API_NAME, path: `livi_documents/${encodeURIComponent(documentId)}` });
  const response = await restOperation.response;
  // DELETE answers with a small body; tolerate an empty one.
  const result = response?.body ? await processResponse(response) : null;
  await refreshLists();
  return result;
}
